import React, {useState,useEffect,useRef, useMemo, useCallback} from "react";
import "./Grade5MathLesson.css";

// Additional imports
import { Chart } from "chart.js/auto";
import * as THREE from "three";
import LessonUI from "../MathPagesStructure/LessonUI.js";
import { generateQuizQuestion  } from "./Grade5QuizGenerator.js";
// External components
import WritingPad from "../MathTools/WritingPad.js";
import Calculator from "../MathTools/Calculator.js";
import { mathChapterPages } from "../MathLessonPools";
import { doc, setDoc, updateDoc, onSnapshot, increment, serverTimestamp} from "firebase/firestore";
import { db } from "../../firebase";
import { StoryModeQuiz, QuizResultScreen, FinalQuizUI as StoryFinalQuizUI} from "../MathPagesStructure/StoryModeStructure";

/* ------------------------------------------------------------------
   3) CHAPTER → QUIZ TOPIC MAP
------------------------------------------------------------------ */
const chapterTopicMap = {
  1: "bigNumbers",
  2: "fractions",
  3: "decimalsPercent",
  4: "measurementVolume",
  5: "coordinateData",
  6: null,
};

export default function Grade5MathLesson({ user, onAwardTokens }) {
  /* ────────── core slide state ────────── */
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);

  /* ────────── per‑chapter quiz state ────────── */
  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [qScore, setQScore] = useState(0);
  const [quizDone, setQuizDone] = useState(false);

  /* ────────── final quiz state ────────── */
  const [allDone, setAllDone] = useState(false);
  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [finalDone, setFinalDone] = useState(false);
  const tokenGranted = useRef(false);
  const [currentUser, setCurrentUser] = useState(user ?? null);
  /* ────────── tools toggles ────────── */
  const [showCalc, setShowCalc] = useState(false);
  const [showPad, setShowPad] = useState(false);

  /* ────────── interactive‑page state (Chart / ThreeJS / Canvas) ────────── */
  const [percentage, setPercentage] = useState(50);
  const pieChartCanvas = useRef(null);
  const chartRef = useRef(null);

  const [cubeSize, setCubeSize] = useState(3);
  const cubeRef = useRef(null);

  const [points, setPoints] = useState([]);
  const coordRef = useRef(null);

  useEffect(() => {
    if (!user) {
      const cached = JSON.parse(
        localStorage.getItem("loggedInUser") || "null"
      );
      if (cached) setCurrentUser(cached);
    }
  }, [user]);

  /* ----------------------------------
     2. Firebase progress subscription
  ---------------------------------- */
  useEffect(() => {
    if (!user?.uid) return;
    const ref = doc(db, "users", user.uid, "courseProgress", "math_g5");
    const unsub = onSnapshot(ref, snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      if (d.chaptersDone) setAllDone(true);
    });
    return unsub;
  }, [user?.uid]);

  const markChaptersDone = async () => {
    if (!user?.uid) return;
    await setDoc(
      doc(db, "users", user.uid, "courseProgress", "math_g5"),
      { chaptersDone: true, lastUpdated: serverTimestamp() },
      { merge: true }
    );
  };

  /* ----------------------------------
     3. Slides per chapter + progress %
  ---------------------------------- */
  const chapterPageRanges = useMemo(
    () => [
      [0, 4], [5, 8], [9, 10], [11, 12], [13, 15], [16, 16]
    ],
    []
  );
  const [startIdx, endIdx] = chapterPageRanges[chapter - 1];
  const pages = mathChapterPages[5].slice(startIdx, endIdx + 1);
  const currentPage = pages[pageIdx];

  const totalSlides = mathChapterPages[5].length;
  const slidesViewed = useMemo(() => {
    let acc = 0;
    for (let i = 0; i < chapter - 1; i++) {
      const [s, e] = chapterPageRanges[i];
      acc += e - s + 1;
    }
    return acc + pageIdx;
  }, [chapter, pageIdx, chapterPageRanges]);
  const progress = Math.round(((slidesViewed + 1) / totalSlides) * 100);

  /* ----------------------------------
     4. Per‑chapter quiz helpers
  ---------------------------------- */
  const startChapterQuiz = () => {
    const topic = chapterTopicMap[chapter];
    if (!topic) { setAllDone(true); return; }
    setQuiz(Array.from({ length: 8 }, () => generateQuizQuestion(topic)));
    setQIdx(0); setQScore(0); setQuizDone(false); setShowQuiz(true);
  };
  const answerChapter = opt => {
    if (opt === quiz[qIdx].correctAnswer) setQScore(s => s + 1);
    qIdx < quiz.length - 1 ? setQIdx(i => i + 1) : setQuizDone(true);
  };
  const finishChapterQuiz = () => {
    if (qScore < 4) return startChapterQuiz(); // retry

    if (chapter < 6) {
      setChapter(c => c + 1);
      setPageIdx(0);
      setShowQuiz(false);
    } else {
      setAllDone(true);
      setShowQuiz(false);
      markChaptersDone();
    }
  };

  /* ----------------------------------
     5. Final quiz helpers
  ---------------------------------- */
  const startFinalQuiz = () => {
    const used = new Set();
    const arr = [];
    while (arr.length < 50) {
      const q = generateQuizQuestion();
      const key = `${q.question}|${q.correctAnswer}`;
      if (!used.has(key)) { used.add(key); arr.push(q); }
    }
    setFinalQs(arr);
    setFIdx(0); setFScore(0); setFinalDone(false); setShowFinal(true);
  };
  const answerFinal = opt => {
    if (opt === finalQs[fIdx].correctAnswer) setFScore(s => s + 1);
    fIdx < finalQs.length - 1 ? setFIdx(i => i + 1) : setFinalDone(true);
  };
  const writeFinalStats = useCallback( async () => {
    if (!user?.uid || tokenGranted.current) return;
    const pct = (fScore / finalQs.length) * 100;
    const ref = doc(db, "users", user.uid, "courseProgress", "math_g5");
    await setDoc(ref, {
      finalQuizScore: fScore,
      finalQuizTotal: finalQs.length,
      lastCompleted: serverTimestamp(),
      tokensAwarded: pct >= 80
    }, { merge: true });
    if (pct >= 80) {
      await updateDoc(doc(db, "users", user.uid), { tokens: increment(1) });
      onAwardTokens?.(1);
    }
    tokenGranted.current = true;
  }, [user?.uid, fScore, finalQs.length, onAwardTokens]);

 useEffect(() => { if (finalDone) writeFinalStats(); }, [finalDone, writeFinalStats]);

 /* ----------------------------------------
    Interactive setups
  ---------------------------------------- */
  // For percentages page
  useEffect(() => {
    if (currentPage.isPercentageInteractive && pieChartCanvas.current) {
      if (chartRef.current) chartRef.current.destroy();
      const ctx = pieChartCanvas.current.getContext("2d");
      chartRef.current = new Chart(ctx, {
        type: "pie",
        data: {
          labels: ["Filled", "Remaining"],
          datasets: [
            {
              data: [percentage, 100 - percentage],
              backgroundColor: ["#ff7675", "#dfe6e9"],
            },
          ],
        },
      });
    }
  }, [pageIdx, percentage, currentPage]);

  // For volume page
  useEffect(() => {
    if (currentPage.isVolumeInteractive && cubeRef.current) {
      // reset old WebGL
      cubeRef.current.innerHTML = "";
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
      const renderer = new THREE.WebGLRenderer({ canvas: cubeRef.current });
      renderer.setSize(250, 250);

      const geometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);
      const material = new THREE.MeshBasicMaterial({
        color: 0x007bff,
        wireframe: true,
      });
      const cube = new THREE.Mesh(geometry, material);
      scene.add(cube);
      camera.position.z = 5;

      function animate() {
        requestAnimationFrame(animate);
        cube.rotation.x += 0.01;
        cube.rotation.y += 0.01;
        renderer.render(scene, camera);
      }
      animate();
    }
  }, [pageIdx, cubeSize, currentPage]);

  // For coordinate page
  useEffect(() => {
    if (currentPage.isCoordinateInteractive && coordRef.current) {
      const canvas = coordRef.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // axes
      ctx.strokeStyle = "black";
      ctx.beginPath();
      ctx.moveTo(150, 0);
      ctx.lineTo(150, 300);
      ctx.moveTo(0, 150);
      ctx.lineTo(300, 150);
      ctx.stroke();

      // ticks & quadrant labels
      ctx.font = "12px Arial";
      ctx.fillStyle = "black";
      for (let i = -4; i <= 4; i++) {
        const xPos = 150 + i * 30;
        ctx.beginPath();
        ctx.moveTo(xPos, 145);
        ctx.lineTo(xPos, 155);
        ctx.stroke();
        if (i !== 0) ctx.fillText(i.toString(), xPos - 3, 140);

        const yPos = 150 - i * 30;
        ctx.beginPath();
        ctx.moveTo(145, yPos);
        ctx.lineTo(155, yPos);
        ctx.stroke();
        if (i !== 0) ctx.fillText(i.toString(), 160, yPos + 3);
        else ctx.fillText("(0,0)", 160, yPos + 13);
      }
      ctx.fillStyle = "rgba(0,0,0,0.4)";
      ctx.font = "16px Arial";
      ctx.fillText("Q1", 220, 70);
      ctx.fillText("Q2", 60, 70);
      ctx.fillText("Q3", 60, 230);
      ctx.fillText("Q4", 220, 230);

      // plot existing points
      points.forEach(({ x, y }) => {
        ctx.fillStyle = "red";
        ctx.fillRect(x - 2, y - 2, 4, 4);
      });

      // click listener
      canvas.onclick = (e) => {
        const rect = canvas.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        setPoints((prev) => [...prev, { x: cx, y: cy }]);
      };
    }
  }, [pageIdx, points, currentPage]);

  /* ----------------------------------
     7. Quiz UI components (simplified)
  ---------------------------------- */
/* ───────────────────────────────────────────
   Chapter Quiz (story-mode)
───────────────────────────────────────────*/
function ChapterQuizUI() {
  /* “loading” safety */
  if (!quiz[qIdx]) return <p>Loading quiz…</p>;

  return !quizDone ? (
    <StoryModeQuiz
      questions={quiz}
      currentIndex={qIdx}
      score={qScore}
      user ={currentUser}
      onAnswer={answerChapter}
      teacherAvatar ={`${process.env.PUBLIC_URL}/images/Teacher.png`}
      tools={
      <>
       <button onClick={() => setShowCalc(c => !c)}>
        {showCalc ? "Hide Calculator" : "Show Calculator"}
        </button>
      <button onClick={() => setShowPad(p => !p)}>
      {showPad ? "Hide Writing Pad" : "Show Writing Pad"}
      </button>
      {showCalc && <Calculator />}
      {showPad  && <WritingPad />}
      </>
      }
    />
  ) : (
    <QuizResultScreen
      score={qScore}
      total={quiz.length}
      onContinue={finishChapterQuiz}
      continueLabel="Continue"
    />
  );
}


/* ───────────────────────────────────────────
   Final Quiz (story-mode)
───────────────────────────────────────────*/
function FinalQuizUI() {
  if (!finalQs[fIdx]) return <p>Loading final quiz…</p>;

  return (
    <StoryFinalQuizUI
      questions={finalQs}
      currentIndex={fIdx}
      score={fScore}
      onAnswer={answerFinal}
      quizFinished={finalDone}
      onReturn={() => setShowFinal(false)}
      user={currentUser}
      teacherAvatar  ={`${process.env.PUBLIC_URL}/images/Teacher.png`}
    >
      {/* same tool tray as chapter-quiz */}
      <div style={{ marginTop: 16 }}>
        <button onClick={() => setShowCalc(c => !c)}>
          {showCalc ? "Hide Calculator" : "Show Calculator"}
        </button>
        <button onClick={() => setShowPad(p => !p)}>
          {showPad ? "Hide Writing Pad" : "Show Writing Pad"}
        </button>
        {showCalc && <Calculator />}
        {showPad  && <WritingPad />}
      </div>
    </StoryFinalQuizUI>
  );
}

  /* ----------------------------------
     8. JSX
  ---------------------------------- */
  return (
    <div className="grade5-math-page">
      <div className="grade5-math-page-scale">
        {/* progress bar */}
        <div style={{ margin: 12 }}>
          <div style={{ background: "#ddd", borderRadius: 10, height: 20, width: "80%", margin: "0 auto" }}>
            <div style={{ width: `${progress}%`, height: 20, borderRadius: 10, background: "#4caf50", transition: "width .3s" }} />
          </div>
          <p>{progress}% complete</p>
        </div>

        {/* jump nav */}
        {allDone && !showFinal && !showQuiz && (
          <div style={{ textAlign: "center", margin: 16 }}>
            <p>All chapters done – review or take final quiz.</p>
            {[1,2,3,4,5,6].map(ch => (
              <button key={ch} style={{ margin: 4 }} onClick={() => { setChapter(ch); setPageIdx(0); }}>Ch {ch}</button>
            ))}
            <button onClick={startFinalQuiz}>Final Quiz</button>
          </div>
        )}

        {/* lesson OR chapter quiz */}
        {!allDone && !showFinal && (
          showQuiz ? <ChapterQuizUI /> :
          <LessonUI
            pages={pages}
            currentPageIndex={pageIdx}
            onPrevPage={() => setPageIdx(i => i - 1)}
            onNextPage={() => setPageIdx(i => i + 1)}
            onStartQuiz={startChapterQuiz}
            backTo="/math"
            renderExtraSection={({ page }) => {
  /* ── Interactive % Pie ───────────────────────────── */
  if (page.isPercentageInteractive) {
    return (
      <div style={{ marginTop: 12 }}>
        <input
          type="range"
          min="0"
          max="100"
          value={percentage}
          onChange={e => setPercentage(+e.target.value)}
          style={{ width: 120 }}
        />
        <span style={{ marginLeft: 8 }}>{percentage}%</span>
        <div style={{ marginTop: 6 }}>
          <canvas ref={pieChartCanvas} width="120" height="120" />
        </div>
      </div>
    );
  }

  /* ── Interactive Volume Cube ─────────────────────── */
  if (page.isVolumeInteractive) {
    return (
      <div style={{ marginTop: 12 }}>
        <p><strong>Cube side:</strong> {cubeSize}</p>
        <input
          type="range"
          min="1"
          max="5"
          value={cubeSize}
          onChange={e => setCubeSize(+e.target.value)}
        />
        <canvas
          ref={cubeRef}
          width="260"
          height="260"
          style={{ border: "1px solid #aaa", marginTop: 8 }}
        />
        <p>
          Volume = {cubeSize} × {cubeSize} × {cubeSize}&nbsp;=&nbsp;
          {Math.pow(cubeSize, 3)}
        </p>
      </div>
    );
  }

  /* ── Interactive Coordinate Grid ─────────────────── */
  if (page.isCoordinateInteractive) {
    return (
      <div style={{ marginTop: 12 }}>
        <canvas
          ref={coordRef}
          width="320"
          height="320"
          style={{ border: "1px solid #000" }}
        />
        <p>Click anywhere on the grid to plot points.</p>
      </div>
    );
  }

  /* default – no extra section */
  return null;
}}

          />
        )}

        {/* start final button */}
        {allDone && !showFinal && (
          <div style={{ textAlign: "center" }}>
            <button onClick={startFinalQuiz}>Start Final Quiz</button>
          </div>
        )}

        {/* final quiz view */}
        {showFinal && <FinalQuizUI />}
      </div>
    </div>
  );
}
